<template>
  <div class="quote-form-widget">
    <div class="p-3">
      <div class="relative">
        <div class="quote-hero-banner">
          <div
            class="quote-hero-image"
            :style="{
              backgroundImage: `url(${backgroundImgUrl})`,
            }" />
        </div>

        <div class="absolute top-0 left-0 w-full h-full">
          <div
            class="flex gap-6 lg:gap-[3.75rem] xl:gap-[7.5rem] max-w-xlg w-full m-auto p-6 lg:p-[1.625rem] h-full wl:lg:flex-row-reverse">
            <div class="flex items-center">
              <div v-if="quoteSteps.length" class="flex flex-col gap-3 md:gap-4 lg:gap-8">
                <div v-for="(step, index) in quoteSteps" :key="index" class="flex items-center gap-3 md:gap-4 lg:gap-8">
                  <div>
                    <div class="number-badge">
                      <span>
                        {{ index + 1 }}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p class="quote-step" v-html="step"></p>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="!config.getIsMobile" class="ml-auto w-full lg:max-w-xl hidden lg:block h-fit">
              <QuoteForm />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="config.getIsMobile" class="block lg:hidden p-2 lg:p-0 wl:mb-8">
      <QuoteForm />
      <div v-if="!isWhitelabel" class="px-4">
        <hr class="mt-1" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { TranslatableString } from '~/types/widgets/widgetTypes'
import getWidgetTranslatedMediaUrl from '~/utils/getWidgetTranslatedMediaUrl'
import { useStoreConfig } from '~/store/storeConfig'

const { t } = useLang()

const props = defineProps<{
  backgroundImage: TranslatableString
}>()
const config = useStoreConfig()
const { isWhitelabel } = useTheme()
const quoteSteps = computed<string[]>(() => {
  return [t('quote_steps.step_1'), t('quote_steps.step_2'), t('quote_steps.step_3'), t('quote_steps.step_4')]
})

const image = useImage()

const backgroundImgUrl = computed(() => {
  const originalUrl = getWidgetTranslatedMediaUrl(props.backgroundImage)

  if (!originalUrl) {
    return ''
  }

  return image(originalUrl, {
    format: 'webp',
    quality: 75,
  })
})
</script>

<style lang="postcss" scoped>
.quote-form-widget {
  @apply w-full flex justify-center flex-col lg:mb-12;

  .quote-hero-banner {
    @apply max-w-2xlg h-[200px] sm:h-[400px] lg:h-[700px] m-auto;
  }

  .quote-hero-image {
    @apply w-full bg-no-repeat bg-center bg-cover h-[200px] sm:h-[400px] lg:h-[700px] rounded-sm lg:rounded-md;
  }

  .quote-step {
    @apply text-white text-sm md:text-2xl lg:text-3xl xl:text-4xl font-primary max-w-md leading-tight;

    :deep(b),
    :deep(strong) {
      @apply font-primary;
    }
  }

  .number-badge {
    @apply flex w-[1.5625rem] lg:w-[3.75rem] h-[1.5625rem] lg:h-[3.75rem];

    span {
      @apply text-white font-primary m-auto font-bold text-lg lg:text-5xl;
    }
  }
}

.number-badge {
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;
  background-image: url('@/assets/images/bg-badge.svg');
}

.wl {
  .quote-form-widget {
    .quote-hero-image {
      @apply !rounded-sm;
    }
  }
}
</style>
